import React, { useState, useRef, useEffect, useMemo } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useFullScreenHandle } from 'react-full-screen'
import { Box, Stack } from '@mui/material';

import { PMTILES_URL } from '../const/general'
import { PMTILES_PREF_KEY } from '../config/config'

import {
    RAIN_H1_COLOR,
    TYPHOON_COLOR,
} from '../const/color'

import {
    setTyphoonColor,
} from '../func/color';

import {
    BoxCenterStart,
} from '../style/box'

import {
    StyleFullScreen,
    MapWrapper
} from '../style/map_components';
import MapTimeSlider from '../item/slider_map'
import SliderTimePanel from '../item/slider_time_panel'

import MapLegendIcon from '../item/map_legend_icon'
import MapFullscreenIcon from '../item/map_fullscreen_icon'
import RainCloudButton from '../item/rain_cloud_button'
import TyphoonButton from '../item/typhoon_button'
import MapMessage from '../item/map_message'
import '../style/legend_map.css';


const MapRiskAnalysis = (props) => {
    const sourceSetting = useMemo(() => {
        return {
            "osm": {
                "type": "raster",
                "tiles": ["https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg"],
                "tileSize": 256,
                "attribution": "MapLibre | 地図の出典：<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
            },
            "city": {
                "type": "vector",
                "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/city/{z}/{x}/{y}.mvt`],
            },
            "msh_50km": {
                "type": "vector",
                "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/msh_50km/{z}/{x}/{y}.mvt`],
            },
            "h1_msh": {
                "type": "vector",
                "tiles": [`${PMTILES_URL}/latest/${props.strDateTimePath}/h1_msh/{z}/{x}/{y}.mvt`],
            },
        };
    }, [props.strDateTimePath, props.prefCode]);

    // 背景地図の設定
    const baseLayer = [
        {
            "id": "osm-tiles",
            "type": "raster",
            "source": "osm",
            "minzoom": 0,
            "maxzoom": 24,
        }
    ];

    // PMTilesから取得して表示、非表示を制御するレイヤ達  
    const otherLayers =
        [
            {
                "id": "h1_msh",
                "source": "h1_msh",
                "source-layer": "h1_msh",
                "type": "fill",
                "paint": {
                    "fill-color": "#5c9fed",
                    "fill-opacity": 0.7,
                    'fill-outline-color': 'rgba(0,0,0,0)',
                },
                "layout": {
                    "visibility": "none" // 最初から表示する場合は "visible"に
                }
            },
            {
                "id": "msh_50km",
                "source": "msh_50km",
                "source-layer": "msh_50km",
                "type": "fill",
                "paint": {
                    "fill-color": 'rgba(0,0,0,0)',
                    "fill-opacity": 0.7,
                    'fill-outline-color': 'rgba(0,0,0,0)',
                },
                "layout": {
                    "visibility": "visible" // 最初から表示する場合は "visible"に
                }
            },
            {
                "id": "city",
                "source": "city",
                "source-layer": "city",
                "type": "line",
                "paint": {
                    'line-opacity': 0.8,
                    'line-color': '#fff',
                    'line-width': 1.5,
                },
                "layout": {
                    "visibility": "visible" // 最初から表示する場合は "visible"に
                }
            },
        ];
    const [isShowH1, setIsShowH1] = useState(true);
    const mapContainer = useRef(null);
    const map = useRef(null);

    // 監視用Ref
    const selectCitysRef = useRef(props.selectCitys);
    const mapDataRef = useRef(props.mapData);

    const mapStyle = {
        height: '100%',
        width: '100%',
        margin: '0 auto',
    }

    // 全画面表示切り替え
    const fullscreenHandle = useFullScreenHandle()

    const layersSetting = [...baseLayer, ...otherLayers]

    let hoveredId = null

    // レイヤー切り替え
    const switchLayer = () => {
        if (map.current && map.current.isStyleLoaded()) {
            if (isShowH1 && props.chooseNum < 8) {
                // 1時間雨量が選択されている場合
                map.current.setLayoutProperty('h1_msh',
                    'visibility', 'visible');
            } else {
                // 1時間雨量が選択解除された場合
                map.current.setLayoutProperty('h1_msh',
                    'visibility', 'none');
            }
        }
    }

    // ベースレイヤーとなる市町村のラインを設定
    const setBaseCityLayer = () => {
        const matchExpression_line = ['match', ['get', 'id']];
        if (!selectCitysRef.current || selectCitysRef.current === undefined || selectCitysRef.current === null || selectCitysRef.current.length < 1) {
            map.current.setPaintProperty('city', 'line-color', 'rgba(0,0,0,0)');
            return
        }
        for (const item of selectCitysRef.current) {
            matchExpression_line.push(item, '#fff')
        }
        matchExpression_line.push('rgba(0, 0, 0, 0)');
        map.current.setPaintProperty('city', 'line-color', matchExpression_line);
    }

    // 台風メッシュのスタイル変更
    const setMshStyle = () => {

        // データがなければ透明化して終了
        if (!mapDataRef.current || mapDataRef.current === undefined || mapDataRef.current === null || mapDataRef.current.length < 1) {
            map.current.setPaintProperty('msh_50km', 'fill-color', 'rgba(0,0,0,0)');
            return;
        }

        // 色設定
        const matchExpression_fill = ['match', ['get', 'id'], -1, 'rgba(0,0,0,0)'];
        for (const item of mapDataRef.current) {
            if (item.code !== null && item.val[props.chooseNum] !== null && item.val[props.chooseNum] > 0) {
                matchExpression_fill.push(
                    Number(item.code),
                    setTyphoonColor(item.val[props.chooseNum]).color);
            }
        }

        matchExpression_fill.push('rgba(0,0,0,0)');

        // 塗り
        map.current.setPaintProperty('msh_50km', 'fill-color', matchExpression_fill);
        map.current.setPaintProperty('msh_50km', 'fill-opacity', 0.7);
    };

    // 雨量タイルのスタイル変更
    const setH1Style = () => {
        const index = props.chooseNum * 3
        if (isShowH1 && index < 22) {

            map.current.setPaintProperty('h1_msh', 'fill-color', [
                'case',
                ['==', ['get', 'choose_num'], index],
                [
                    'match',
                    ['get', 'sint_value_cls_num'],
                    0, RAIN_H1_COLOR.LEVEL_1.color,
                    1, RAIN_H1_COLOR.LEVEL_2.color,
                    2, RAIN_H1_COLOR.LEVEL_3.color,
                    3, RAIN_H1_COLOR.LEVEL_4.color,
                    4, RAIN_H1_COLOR.LEVEL_5.color,
                    5, RAIN_H1_COLOR.LEVEL_6.color,
                    6, RAIN_H1_COLOR.LEVEL_7.color,
                    7, RAIN_H1_COLOR.LEVEL_8.color,
                    RAIN_H1_COLOR.OTHER.color
                ],
                'rgba(0,0,0,0)',
            ]);
        }
    };

    // レイヤーの色分け
    const setLayerStyle = () => {

        if (!map.current || !map.current.isStyleLoaded()) return;
        switchLayer()

        // ベースレイヤーとなる市町村のラインを設定
        setBaseCityLayer()

        // 台風メッシュのスタイル変更
        setMshStyle()

        // 雨量タイルのスタイル変更
        setH1Style()
    }

    // レイヤーマウスホバー時の処理
    const handleMouseMove = (e) => {

        const feature = e.features[0];
        if (!feature) return;
        const id = feature.properties.id;

        // すでにホバー中なら無視
        if (hoveredId === id) return;

        if (!mapDataRef.current || mapDataRef.current === undefined || mapDataRef.current === null || mapDataRef.current.length < 1) return

        const data = mapDataRef.current.find(item => item.code === Number(id));

        // 前のポップアップを削除
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
        }
        const index = props.chooseNum

        if (data.val[index] > 0) {
            map.current.getCanvas().style.cursor = 'pointer';
        } else {
            map.current.getCanvas().style.cursor = '';
            return;
        }
        hoveredId = id;

        // ポップアップ表示
        if (data.val[index] > 0) {
            const popup = new maplibregl.Popup({
                closeButton: false,
                closeOnClick: false
            })

                .setLngLat(e.lngLat)
                .setHTML(`
                        <div>台風名：<strong>${data.typhoon_name[index]} </strong></div>
                        <div>確率：<strong>${data.val[index]} %</strong></div>
                    `)
                .addClassName('custom-popup')
                .addTo(map.current);

            map.current.__hoverPopup = popup;
        }
    };

    // レイヤーマウスアウト時の処理
    const handleMouseLeave = () => {
        map.current.getCanvas().style.cursor = '';
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
            hoveredId = null
        }
    };


    // ロード時の処理
    const handleLoad = () => {
        if (!map.current) return;
        setLayerStyle()
    };

    // マップ初期化処理
    useEffect(() => {
        if (map.current) return; // initialize map only once

        map.current = new maplibregl.Map({
            container: mapContainer.current,
            center: props.currentLngLat,
            zoom: 4,
            style: {
                version: 8,
                sources: sourceSetting,
                layers: layersSetting
            },
            attributionControl: false
        });
        map.current.addControl(new maplibregl.NavigationControl({
            // コンパス表示なし
            showCompass: false,
            showZoom: true,
        }), 'top-left');
        map.current.addControl(new maplibregl.AttributionControl({
            // Attributionは折りたたまない
            compact: false,
        }), 'bottom-right');

        map.current.on('load', handleLoad);
        map.current.on('mouseenter', 'msh_50km', (e) => { handleMouseMove(e) });
        map.current.on('mousemove', 'msh_50km', (e) => { handleMouseMove(e) });
        map.current.on('mouseleave', 'msh_50km', handleMouseLeave);

        return () => {
            if (!map.current) return;
            map.current.off('load', handleLoad);
            map.current.off('mouseenter', 'msh_50km', (e) => { handleMouseMove(e) });
            map.current.off('mousemove', 'msh_50km', (e) => { handleMouseMove(e) });
            map.current.off('mouseleave', 'msh_50km', handleMouseLeave);

            map.current.off('styledata', setLayerStyle());
            // map.current.getCanvas().removeEventListener('mouseleave', handleMouseLeave);
            map.current.remove();
            map.current = null;
        };
    }, []);

    // 市町村チェックボックス変更時に呼ばれる
    useEffect(() => {

        selectCitysRef.current = props.selectCitys

        if (mapDataRef.current && mapDataRef.current !== undefined && mapDataRef.current !== null && mapDataRef.current.length > 0) {
            if (map.current?.isStyleLoaded()) {
                setLayerStyle();
            }
        }
    }, [props.selectCitys]);

    // データ更新時
    // 市町村チェックボックス変更時、
    // レイヤー切り替え時
    // リセットボタンで個別の選択が解除されたときも呼ばれる
    useEffect(() => {
        mapDataRef.current = props.mapData

        if (!map.current) return;
        const interval = setInterval(() => {
            if (map.current?.isStyleLoaded()) {
                setLayerStyle();
                clearInterval(interval);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [
        props.mapData, props.chooseNum, isShowH1
    ]);

    return (
        <StyleFullScreen
            handle={fullscreenHandle}
            enabled={true}
        >
            <Stack direction="column"
                sx={{ height: '100%', minHeight: 0 }} >
                <MapWrapper sx={{ flex: 1, minHeight: 0 }}>
                    <div
                        ref={mapContainer}
                        className=""
                        style={mapStyle} />
                    <RainCloudButton
                        top={'10px'}
                        isShowH1={isShowH1}
                        setIsShowH1={setIsShowH1}
                        chooseNum={props.chooseNum}
                    />
                    {/* メッセージをボタンの左に */}
                    {props.chooseNum > 7 &&
                        <MapMessage top={"10px"} message={`雨雲は ${props.dates[7]} 以降は表示できません`} />
                    }
                    {props.typhoonLayer.map((typhoon, index) => (
                        <TyphoonButton
                            key={typhoon.typhoon_number}
                            top={`${10 + 40 * (index + 1)}px`}
                            number={typhoon.typhoon_number % 100} // 末尾2桁
                            isShowTyphoon={typhoon.select}
                            index={index}
                            setTyphoonLayer={props.setTyphoonLayer}
                        />
                    ))}

                    <MapLegendIcon
                        top={`${10 + 40 * (props.typhoonLayer.length + 1)}px`}
                        sections={[
                            {
                                key: "typhoon",
                                label: "台風の暴風域に入る確率",
                                colorMap: TYPHOON_COLOR
                            },
                            {
                                key: "h1Rain",
                                label: "１時間雨量",
                                colorMap: RAIN_H1_COLOR
                            }
                        ]}
                    />
                    <MapFullscreenIcon
                        fullscreenHandle={fullscreenHandle}
                        top={`${10 + 40 * (props.typhoonLayer.length + 2)}px`}
                    />
                </MapWrapper>
                <BoxCenterStart >
                    <SliderTimePanel
                        selectDateTime={props.dates[props.chooseNum]}
                        chooseNum={props.chooseNum}
                        setChooseNum={props.setChooseNum}
                        dates={props.dates}
                    />
                </BoxCenterStart>
                <Box mr={2}>
                    <MapTimeSlider
                        markStyles={[
                            { index: 2, color: "rgb(192, 37, 24)" },
                            { index: 7, color: "rgb(2, 50, 119)" },
                        ]}
                        chooseNum={props.chooseNum}
                        setChooseNum={props.setChooseNum}
                        dates={props.dates}
                    />
                </Box>
            </Stack>
        </StyleFullScreen>
    );
};

export default MapRiskAnalysis;

